import React, { useId, useState, useCallback } from 'react';
import { componentThemes } from '../../../design-system/theme';

/**
 * Input types supported by the component
 */
type InputType = 'text' | 'number' | 'email' | 'password';

/**
 * Input variant types for different styling approaches
 */
type InputVariant = 'default' | 'glass' | 'minimal';

/**
 * Props interface for the enhanced Input component
 */
interface InputProps {
  /** Input label text */
  label?: string;
  /** Input placeholder text */
  placeholder?: string;
  /** Input value */
  value: string | number;
  /** Change handler function */
  onChange: (value: string) => void;
  /** Input type */
  type?: InputType;
  /** Input variant for styling */
  variant?: InputVariant;
  /** Whether to use floating label design */
  floatingLabel?: boolean;
  /** Whether input is disabled */
  disabled?: boolean;
  /** Whether input is required */
  required?: boolean;
  /** Error message to display */
  error?: string;
  /** Helper text to display */
  helperText?: string;
  /** Minimum value for number inputs */
  min?: string | number;
  /** Maximum value for number inputs */
  max?: string | number;
  /** Step value for number inputs */
  step?: string | number;
  /** Prefix text (displayed inside input) */
  prefix?: string;
  /** Suffix text (displayed inside input) */
  suffix?: string;
  /** Additional CSS classes for the input */
  className?: string;
  /** Additional CSS classes for the container */
  containerClassName?: string;
  /** Callback for focus events */
  onFocus?: () => void;
  /** Callback for blur events */
  onBlur?: () => void;
}

/**
 * Enhanced Input Component with floating labels and modern design
 * Features glassmorphism effects, smooth animations, and design system integration
 */
const Input: React.FC<InputProps> = ({
  label,
  placeholder,
  value,
  onChange,
  type = 'text',
  variant = 'default',
  floatingLabel = false,
  disabled = false,
  required = false,
  error,
  helperText,
  min,
  max,
  step,
  prefix,
  suffix,
  className = '',
  containerClassName = '',
  onFocus,
  onBlur
}) => {
  const [focused, setFocused] = useState(false);
  const inputId = useId();
  
  // Check if input has content (for floating label animation)
  const hasContent = value !== '' && value !== undefined && value !== null;
  const shouldFloatLabel = floatingLabel && (focused || hasContent);

  // Handle input change with validation
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  }, [onChange]);

  // Handle focus events
  const handleFocus = useCallback(() => {
    setFocused(true);
    onFocus?.();
  }, [onFocus]);

  // Handle blur events
  const handleBlur = useCallback(() => {
    setFocused(false);
    onBlur?.();
  }, [onBlur]);

  // Get variant-specific styles using design system
  const getVariantStyles = () => {
    const baseStyles = {
      transition: 'var(--transition-all)',
      borderRadius: 'var(--radius-lg)',
    };

    switch (variant) {
      case 'glass':
        return {
          ...baseStyles,
          background: 'var(--bg-glass)',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${error ? 'var(--border-error)' : 'var(--border-secondary)'}`,
          boxShadow: error 
            ? 'var(--shadow-error)'
            : focused 
              ? '0 0 0 3px var(--interactive-primary-hover)'
              : 'var(--shadow-sm)',
        };
      case 'minimal':
        return {
          ...baseStyles,
          background: 'transparent',
          border: 'none',
          borderBottom: `2px solid ${error ? 'var(--border-error)' : focused ? 'var(--interactive-primary)' : 'var(--border-primary)'}`,
          borderRadius: '0',
        };
      default:
        return {
          ...baseStyles,
          ...componentThemes.input.default,
          border: `1px solid ${error ? 'var(--border-error)' : focused ? 'var(--border-focus)' : 'var(--border-primary)'}`,
          boxShadow: error 
            ? 'var(--shadow-error)'
            : focused 
              ? '0 0 0 3px rgba(59, 130, 246, 0.1)'
              : 'var(--shadow-sm)',
        };
    }
  };

  // Base input classes with animations
  const baseInputClasses = [
    'w-full',
    'transition-all',
    'duration-200',
    'ease-out',
    'focus:outline-none',
    'disabled:opacity-50',
    'disabled:cursor-not-allowed',
    'relative',
  ];

  // Responsive padding classes
  const paddingClasses = [
    floatingLabel ? 'pt-6 pb-2' : 'py-3',
    prefix ? 'pl-10' : 'pl-4',
    suffix ? 'pr-10' : 'pr-4',
  ];

  // Combine input classes
  const inputClasses = [
    ...baseInputClasses,
    ...paddingClasses,
    'text-base',
    'placeholder:text-gray-400',
    'placeholder:transition-opacity',
    'placeholder:duration-200',
    focused && floatingLabel ? 'placeholder:opacity-0' : 'placeholder:opacity-100',
    className
  ].join(' ');

  // Label animation classes
  const labelClasses = [
    'absolute',
    'left-4',
    'transition-all',
    'duration-200',
    'ease-out',
    'pointer-events-none',
    shouldFloatLabel ? 'top-2 text-xs' : 'top-1/2 -translate-y-1/2 text-base',
    required ? "after:content-['*'] after:ml-1 after:text-error" : '',
  ].join(' ');

  return (
    <div className={`space-y-1 ${containerClassName}`}>
      {/* Traditional Label (when not using floating label) */}
      {label && !floatingLabel && (
        <label
          htmlFor={inputId}
          className={`block text-sm font-medium transition-colors ${
            required ? "after:content-['*'] after:ml-1 after:text-error" : ''
          }`}
          style={{ color: 'var(--text-primary)' }}
        >
          {label}
        </label>
      )}

      {/* Input Container with Floating Label Support */}
      <div className="relative group">
        {/* Input Field */}
        <input
          id={inputId}
          type={type}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={floatingLabel ? (shouldFloatLabel ? '' : ' ') : placeholder}
          disabled={disabled}
          required={required}
          min={min}
          max={max}
          step={step}
          className={inputClasses}
          style={getVariantStyles()}
        />

        {/* Floating Label */}
        {label && floatingLabel && (
          <label
            htmlFor={inputId}
            className={labelClasses}
            style={{ 
              color: shouldFloatLabel 
                ? 'var(--interactive-primary)' 
                : 'var(--text-tertiary)' 
            }}
          >
            {label}
          </label>
        )}

        {/* Prefix */}
        {prefix && (
          <span 
            className="absolute left-3 top-1/2 -translate-y-1/2 text-sm pointer-events-none transition-colors duration-200"
            style={{ color: focused ? 'var(--interactive-primary)' : 'var(--text-secondary)' }}
          >
            {prefix}
          </span>
        )}

        {/* Suffix */}
        {suffix && (
          <span 
            className="absolute right-3 top-1/2 -translate-y-1/2 text-sm pointer-events-none transition-colors duration-200"
            style={{ color: focused ? 'var(--interactive-primary)' : 'var(--text-secondary)' }}
          >
            {suffix}
          </span>
        )}

        {/* Focus Ring Effect */}
        <div 
          className={`absolute inset-0 rounded-lg pointer-events-none transition-opacity duration-200 ${
            focused && !error ? 'opacity-100' : 'opacity-0'
          }`}
          style={{
            background: 'linear-gradient(45deg, var(--interactive-primary)/10, var(--interactive-secondary)/10)',
          }}
        />

        {/* Animated Border for Glass Variant */}
        {variant === 'glass' && (
          <div 
            className={`absolute inset-0 rounded-lg pointer-events-none transition-opacity duration-300 ${
              focused ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              background: 'linear-gradient(45deg, var(--interactive-primary)/20, transparent, var(--interactive-secondary)/20)',
              padding: '1px',
            }}
          >
            <div 
              className="w-full h-full rounded-lg"
              style={{ background: 'var(--bg-glass)' }}
            />
          </div>
        )}
      </div>

      {/* Helper Text */}
      {helperText && !error && (
        <p 
          className="text-xs transition-colors duration-200" 
          style={{ color: 'var(--text-tertiary)' }}
        >
          {helperText}
        </p>
      )}

      {/* Error Message with Animation */}
      {error && (
        <p 
          className="text-xs animate-slideInUp" 
          role="alert"
          style={{ 
            color: 'var(--interactive-danger)',
            animation: 'slideInUp 0.2s ease-out' 
          }}
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default Input;